// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

// FHIR Patient.birthDate is `YYYY-MM-DD` (no timezone). Parsing it with
// `new Date('YYYY-MM-DD')` yields UTC midnight, which renders as the prior
// day for any local timezone west of UTC — off-by-one age and DOB display
// near month/year boundaries. These helpers parse the date as local-time
// calendar fields.

interface ParsedDate {
  year: number;
  month: number; // 1-12
  day: number;
}

function parseFhirDate(birthDate: string): ParsedDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthDate);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

export function calculateAgeYears(birthDate: string | undefined): number | null {
  if (!birthDate) return null;
  const parsed = parseFhirDate(birthDate);
  if (!parsed) return null;
  const today = new Date();
  let age = today.getFullYear() - parsed.year;
  const monthDiff = today.getMonth() + 1 - parsed.month;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < parsed.day)) {
    age--;
  }
  return age;
}

export function formatBirthDate(
  birthDate: string | undefined,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
  locale = 'en-US'
): string | null {
  if (!birthDate) return null;
  const parsed = parseFhirDate(birthDate);
  if (!parsed) return null;
  // Construct as local date (month is 0-indexed for the Date constructor).
  const local = new Date(parsed.year, parsed.month - 1, parsed.day);
  return local.toLocaleDateString(locale, options);
}
